import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import type { ContextLines, LessonDataSource, PartLines, SlideCount } from "@ai-teach/utils";

export type DataSource = LessonDataSource;

export interface BookLessonRef {
  id: string;
  seriesSlug: string;
  levelId: string;
  componentId: string;
  lesson: number;
  printedLabel: string;
  /** The main vocabulary only; a sub-list of the book's own is counted under `subListCounts`. */
  wordCount: number;
  subListCounts: Record<string, number>;
  grammarCount: number;
  grammarExampleCount: number;
  passageCount: number;
  dialogueCount: number;
  pageLineCount: number;
}

export type { ContextLines, PartLines, SlideCount };

export interface ReferenceSlide {
  id: string;
  name: string;
  template: SlideTemplate;
  tagIds: string[];
  slide: SlideFields;
}

export interface SubslideParent {
  template: SlideTemplate;
  tagId: string;
  each: number;
}

/**
 * A section reads what it checks, so one that checked nothing is written from the example slide
 * alone — worth warning about, but the author's call: a prompt can be all a section needs.
 */
export function readsNothing(section: {
  parent?: SubslideParent;
  context: DataSource[];
  sectionScopes?: string[];
  subLists?: string[];
}): boolean {
  if (section.parent) return false;
  return (
    section.context.length === 0 &&
    (section.sectionScopes ?? []).length === 0 &&
    (section.subLists ?? []).length === 0
  );
}

export interface GeneratorSection {
  id: string;
  name?: string;
  /** The saved section this one follows, so a preset picks up its later edits. */
  sectionPresetId?: string;
  template: SlideTemplate;
  referenceSlide: ReferenceSlide;
  context: DataSource[];
  /** The book sections this reads, children included. */
  sectionScopes?: string[];
  /** The book's own sub-lists of words this reads, beside the main vocabulary. */
  subLists?: string[];
  /** This section's own lines for the sources it reads, in place of the book's. */
  contextLines?: ContextLines[];
  /** This section's own lines for the parts it reads, in place of what the book prints there. */
  partLines?: PartLines[];
  count?: SlideCount;
  parent?: SubslideParent;
  prompt: string;
  /** Shuffles which slot of the reference layout holds the answer, slide by slide. */
  randomizeChoicePositions?: boolean;
  /** Vocabulary only: the example sentence is written for this lesson; off leaves it blank. */
  generateSentence?: boolean;
  /** What every slide this section writes is called, before any number is appended. */
  slideName: string;
  /** Appends each slide's position in the section, from 1, to the slide name. */
  numberSlides: boolean;
}
